import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";

import { MainLayout, Responsive } from "../../../layout";
import { getPhoneNumber, getEmail, getWorkingItemsFromChecks } from "../../../util/common";
import { APPOINTMENT_NOTE_TYPES } from "../../../util/notes";

import { Header, NavigationButtons, Order, SideMenu, Support, TiresOverview } from "../components";

import { PopupMessage, FixedItems, NotFixedItems, WorkingItems } from "./components";
import { SCREENS } from "./util/screens";

class RepairOverviewV2 extends Component {
  constructor(props) {
    super(props);
    const { communication } = this.props;

    const billNote = communication.appointment.notes.find(note => note.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.BILL_NOTE && note.attachments.length);

    this.state = {
      defaultScreen: SCREENS.FIRST_SCREEN,
      fixedItems: FixedItems.filterFixedItems(communication.appointment.interventions),
      notFixedItems: NotFixedItems.filterNotFixedItems(communication.appointment.interventions),
      workingItems: WorkingItems.filterWorkingsItems(getWorkingItemsFromChecks(communication.appointment.checks)),
      bill: billNote?.attachments[0],
    };
  }

  getCurrentScreen = () => {
    let { screen } = this.props;
    if (screen) {
      screen = parseInt(screen);
      if (screen === SCREENS.SUPPORT || (screen >= SCREENS.FIRST_SCREEN && screen <= SCREENS.LAST_SCREEN)) return screen;
    }

    return this.state.defaultScreen;
  };

  handleGoToScreen = currentScreen => {
    if (!currentScreen || currentScreen < SCREENS.FIRST_SCREEN || currentScreen > SCREENS.LAST_SCREEN) currentScreen = this.state.defaultScreen;

    this.props.history.push("/" + currentScreen + this.props.location.hash);
    window.scrollTo(0, 0);
  };

  shouldRenderScreen = screen => {
    const { fixedItems, notFixedItems, workingItems } = this.state;

    switch (screen) {
      case SCREENS.FIXED_ITEMS:
        return fixedItems.length > 0;
      case SCREENS.NOT_FIXED_ITEMS:
        return notFixedItems.length > 0;
      case SCREENS.OTHER_ITEMS:
        return workingItems.length > 0;
      default:
        return false;
    }
  };

  handleGoToNextScreen = () => {
    const { fixedItems } = this.state;
    const currentScreen = this.getCurrentScreen();
    let shift = 1;

    if (currentScreen + shift === SCREENS.FIXED_ITEMS) {
      if (this.shouldRenderScreen(SCREENS.FIXED_ITEMS)) return this.handleGoToScreen(currentScreen + shift);
      else shift++;
    }

    if (currentScreen + shift === SCREENS.NOT_FIXED_ITEMS) {
      if (this.shouldRenderScreen(SCREENS.NOT_FIXED_ITEMS)) return this.handleGoToScreen(currentScreen + shift);
      else shift++;
    }

    if (currentScreen + shift === SCREENS.OTHER_ITEMS) {
      if (this.shouldRenderScreen(SCREENS.OTHER_ITEMS)) return this.handleGoToScreen(currentScreen + shift);
      else shift++;
    }

    const nextScreen = currentScreen + shift === SCREENS.ORDER && !fixedItems.length ? SCREENS.FIRST_SCREEN : currentScreen + shift;

    this.handleGoToScreen(nextScreen);
  };

  handleGoToPreviousScreen = () => {
    const currentScreen = this.getCurrentScreen();
    let shift = 1;

    if (currentScreen - shift === SCREENS.OTHER_ITEMS) {
      if (this.shouldRenderScreen(SCREENS.OTHER_ITEMS)) return this.handleGoToScreen(currentScreen - shift);
      else shift++;
    }

    if (currentScreen - shift === SCREENS.NOT_FIXED_ITEMS) {
      if (this.shouldRenderScreen(SCREENS.NOT_FIXED_ITEMS)) return this.handleGoToScreen(currentScreen - shift);
      else shift++;
    }

    if (currentScreen - shift === SCREENS.FIXED_ITEMS) {
      if (this.shouldRenderScreen(SCREENS.FIXED_ITEMS)) return this.handleGoToScreen(currentScreen - shift);
      else shift++;
    }

    this.handleGoToScreen(currentScreen - shift);
  };

  handleGoToSupportScreen = () => {
    this.setState({ screenBeforeSupport: this.getCurrentScreen() });
    this.props.history.push("/" + SCREENS.SUPPORT + this.props.location.hash);
    window.scrollTo(0, 0);
  };

  handleGoBackFromSupportScreen = () => {
    this.handleGoToScreen(this.state.screenBeforeSupport);
  };

  renderPopups = () => {
    const { communication, settings } = this.props;

    return <PopupMessage type="welcome" communication={communication} settings={settings} />;
  };

  renderMainColumn = () => {
    const { fixedItems, notFixedItems, workingItems } = this.state;
    const { communication, settings, routeKey, t } = this.props;
    const title = t("cc_repair_overview").message || "Repair overview";
    const currentScreen = this.getCurrentScreen();

    return (
      <Responsive showMobile={currentScreen !== SCREENS.ORDER} className="mainContent">
        {currentScreen === SCREENS.SUPPORT && <Support settings={settings} />}
        {currentScreen !== SCREENS.SUPPORT && (
          <>
            <Header
              showFooter
              showPrintDownload
              showStatusLegend
              title={title}
              communication={communication}
              settings={settings}
              routeKey={routeKey}
              showMobile={currentScreen === SCREENS.INFO}
              correctEmail={communication.correct_email || getEmail(communication)}
              correctPhone={communication.correct_phone || getPhoneNumber(communication)}
            />
            <FixedItems fixedItems={fixedItems} settings={settings} communication={communication} showMobile={currentScreen === SCREENS.FIXED_ITEMS} />
            <NotFixedItems notFixedItems={notFixedItems} settings={settings} communication={communication} showMobile={currentScreen === SCREENS.NOT_FIXED_ITEMS} />
            <TiresOverview communication={communication} settings={settings} />
            <WorkingItems workingItems={workingItems} communication={communication} showMobile={currentScreen === SCREENS.OTHER_ITEMS} />
          </>
        )}
      </Responsive>
    );
  };

  renderOrderColumn = side => {
    const { layout } = this.props.settings;
    const { fixedItems, bill } = this.state;

    if (fixedItems.length && ((layout === 1 && side === "right") || (layout === 2 && side === "left"))) {
      const { settings, communication } = this.props;
      const currentScreen = this.getCurrentScreen();

      return (
        <Responsive showMobile={currentScreen === SCREENS.ORDER}>
          <SideMenu
            settings={settings}
            communication={communication}
            supportActive={currentScreen === SCREENS.SUPPORT}
            goToSupportScreen={this.handleGoToSupportScreen}
            goBackFromSupportScreen={this.handleGoBackFromSupportScreen}
          >
            <Order settings={settings} interventions={fixedItems} bill={bill} readOnly={true} />
          </SideMenu>
        </Responsive>
      );
    }

    return null;
  };

  renderNavigation = () => {
    const currentScreen = this.getCurrentScreen();
    return (
      <NavigationButtons
        SCREENS={SCREENS}
        onGoToPreviousScreen={this.handleGoToPreviousScreen}
        onGoToNextScreen={this.handleGoToNextScreen}
        currentScreen={currentScreen}
        backOnly={currentScreen === SCREENS.ORDER}
      />
    );
  };

  render() {
    const { settings, t } = this.props;

    return (
      <>
        <MainLayout
          mobileTitle={t("cc_repair_overview").message || "Repair overview"}
          color={settings.color}
          popups={this.renderPopups()}
          leftColumn={this.renderOrderColumn("left")}
          mainColumn={this.renderMainColumn()}
          rightColumn={this.renderOrderColumn("right")}
          navigation={this.renderNavigation()}
        />
      </>
    );
  }
}

export default withRouter(withTranslation()(RepairOverviewV2));
